import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import classes from "./StandardsDataGrid.scss";
import useAdminStandardsQuery from "./hooks/useAdminStandardsQuery";
import StandardsDataGridHeader from "../StandardsDataGridHeader";
import { useFilters } from "../../../../providers/FilterProvider";
import {
  getMetadata,
  getEdgesFromNodeMap as _getEdgesFromNodeMap,
  getColumnProperties,
} from "./modules/utils";
import StandardsTable from "StandardsTable";
import {
  useStandardsTableProps,
  useStandardsConfigureColumns,
} from "StandardsTable/hooks";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import { useReduxState } from "SubjectStandardsV2/hooks";
import { getCurrentAcademicSetupTypeFromState } from "SubjectStandardsV2/modules/reduxSelectors";
import ACLStore from "lib/aclStore";
import { colors } from "Constants";
import {
  DATA_SET_COLUMNS_SETTING,
  ENABLE_PARENT_STANDARDS_TAGGING,
  ENABLE_STANDARDS_OUTLINE,
} from "Constants/permissionConstants";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import {
  DEFAULT_HIDDEN_COLUMNS,
  NON_CONFIGURABLE_COLUMNS,
} from "./modules/stringConstants";

const cellUtils = {
  getMetadata,
};

const StandardsDataGrid = props => {
  const {
    selectedStandards,
    setSelectedStandards,
    id,
    subjects,
    grades,
    tags,
    showLoaderOverHeader,
    showCourseStandards,
    learningCourseId,
    columns: defaultColumns,
    updateTree,
  } = props;

  const { resetFilters } = useFilters();

  const { isOldSubjectBasedGrading } = useReduxState(
    getCurrentAcademicSetupTypeFromState
  );

  const {
    columnIdsForConfigureModal,
    currentColumns,
    updateColumns,
  } = useStandardsConfigureColumns({
    settingName: DATA_SET_COLUMNS_SETTING,
    moduleName: "StandardsTaggingModal",
    defaultColumns,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
    nonConfigurableColumns: NON_CONFIGURABLE_COLUMNS,
  });

  const { edges, showLoader } = useAdminStandardsQuery({
    id,
    learningCourseId,
    showCourseStandards,
  });

  const columnProperties = useMemo(
    () => getColumnProperties({ columns: defaultColumns }),
    [defaultColumns]
  );

  const {
    columns,
    nodeMap,
    properties,
    sideNavData,
    originalNodeMap,
  } = useStandardsTableProps({
    edges,
    columns: currentColumns,
    cellUtils,
    properties: columnProperties,
  });

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectionColIndex: _.indexOf(columns, DATA_GRID_COLUMN_TYPES.SELECTION),
        selectedStandards,
        isParentTaggingEnabled:
          !isOldSubjectBasedGrading ||
          ACLStore.can(ENABLE_PARENT_STANDARDS_TAGGING),
      });
    },
    [columns, isOldSubjectBasedGrading, selectedStandards]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      const result = handleSelectionChange({
        params,
        selectedRows: selectedStandards,
        setSelectedRows: setSelectedStandards,
      });

      // Update tree with added and removed nodes
      if (result && updateTree) {
        const {
          addedNodes = [],
          removedNodes = [],
          selectedNodes = [],
        } = result;
        updateTree({
          addedNodes,
          removedNodes,
          nodeMap: originalNodeMap,
          selectedNodes,
        });
      }
    },
    [selectedStandards, setSelectedStandards, updateTree, originalNodeMap]
  );
  const showOutline = ACLStore.can(ENABLE_STANDARDS_OUTLINE);

  const gridClasses = classNames(classes.gridBody, {
    [classes.outlineHiddenGridBody]: !showOutline,
  });

  return (
    <StandardsTable
      columns={columns}
      nodeMap={nodeMap}
      properties={properties}
      sideNavData={sideNavData}
      showLoader={showLoader}
      showOutline={showOutline}
    >
      <StandardsTable.Body bodyClass={gridClasses}>
        <StandardsTable.Header>
          <StandardsDataGridHeader
            subjects={subjects}
            grades={grades}
            tags={tags}
            showLoaderOverHeader={showLoaderOverHeader}
            defaultColumns={defaultColumns}
            currentColumns={currentColumns}
            updateColumns={updateColumns}
            columnIdsForConfigureModal={columnIdsForConfigureModal}
            properties={properties}
          />
        </StandardsTable.Header>
        <StandardsTable.Grid
          resetFilters={resetFilters}
          showLoader={showLoader}
          getEdgesFromNodeMap={getEdgesFromNodeMap}
          onCellEdit={onCellEdit}
          freezeColumns={2}
          cellSelectionBorderColor={colors.strokeOne}
        />
      </StandardsTable.Body>
    </StandardsTable>
  );
};

export default StandardsDataGrid;
